import { useState, useEffect } from "react";
// --- INÍCIO: ADIÇÕES ---
import { useParams, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { FiXCircle, FiClock } from 'react-icons/fi';
import { SolidButton } from "../../../../components/SolidButton";
// --- FIM: ADIÇÕES ---

import { useAuth } from "../../../../hooks/AuthContext";
import { useOnboard } from "../../../../hooks/OnboardContext";

import { i18n } from "../../../../localization/i18n";

import CustomToast, { showToast } from "../../../../components/CustomToast";
import { LanguageSelector } from "../../../../components/LanguageSelector";
import { Stepper } from "../../../../components/Stepper";

import { StepOne } from "./Components/StepOne";
import { StepTwo } from "./Components/StepTwo";

import api from "../../../../services/api";

import { countries } from "../../../../utils/countries";
import {
  addressTypes,
  customerSituations,
  personTypes,
} from "../../../../utils/constants";

import {
  IAddressFormData,
  IAuthFormData,
  ILicenseeFormData,
  IPersonFormData,
} from "./dtos.d";

import LogoImg from "../../../../assets/logo.svg";

import {
  Container,
  LogoContainer,
  FormContainer,
  StepperContainer,
  ChangeLanguageContainer,
  ChangePriceContainer,
  PriceSelect,
} from "./styles";
import { IconWrapper, Message, Title, Actions } from "./styles";
import { StepZero } from "./Components/StepZero";


interface IBusinessProposalDTO {
  id: string;
  licensee_name: string;
  licensee_email: string;
}

export function UserRegistration() {
  const { user, signOut, validateCode } = useAuth();
  const { licensee } = useOnboard();

  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [validationState, setValidationState] = useState<'validating' | 'valid' | 'invalid'>('validating');
  const [errorMessage, setErrorMessage] = useState('');
  const [proposal, setProposal] = useState<IBusinessProposalDTO | null>(null);

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [authFormData, setAuthFormData] = useState<IAuthFormData>();
  const [personFormData, setPersonFormData] = useState<IPersonFormData>();
  const [licenseeFormData, setLicenseeFormData] = useState<ILicenseeFormData>();
  const [addressFormData, setAddressFormData] = useState<IAddressFormData>();
  const [companyAddressFormData, setCompanyAddressFormData] = useState<IAddressFormData>();
  const [userId, setUserId] = useState("");
  const [selectedPrice, setSelectedPrice] = useState("");

  const addresses: IAddressFormData[] = new Array(2);

  const steps = [
    {
      label: 'Verificação e aceite de proposta',
      component: (
        <StepZero
          proposal={proposal}
          nextStep={nextStep}
          loading={loading} />
      ),
    },
    {
      label: i18n.t("userRegistration.stepOne"),
      component: (
        <StepOne
          setEmailValidation={setValidatingEmail}
          validatingEmail={validatingEmail}
          formData={authFormData}
          setFormData={setAuthFormData}
          nextStep={nextStep}
          deleteCode={handleDeleteCode}
          prevStep={prevStep}
          loading={loading}
          submitCode={handleSubmitCode}
          validateEmail={handleCreateUserAndSendValidationMail}
          resendCode={resendCode}
        />
      ),
    },
    {
      label: i18n.t("userRegistration.stepTwo"),
      component: (
        <StepTwo
          personFormData={personFormData}
          setPersonFormData={setPersonFormData}
          executeUserCreation={handleCreatePerson}
          licenseeFormData={licenseeFormData}
          setLicenseeFormData={setLicenseeFormData}
          addressFormData={addressFormData}
          companyAddressFormData={companyAddressFormData}
          setAddressFormData={setAddressFormData}
          setCompanyAddressFormData={setCompanyAddressFormData}
          loading={loading}
        />
      ),
    },
  ];

  async function validateInvitationToken() {
    if (!token) {
      setValidationState('invalid');
      setErrorMessage('Nenhum convite encontrado. Verifique o link e tente novamente.');
      return;
    }
    try {
      setValidationState('validating');
      const response = await api.get<IBusinessProposalDTO>(`/business-proposal/complete/${token}`);
      setProposal(response.data);
      setAuthFormData((prev) => ({ 
        ...prev, 
        email: response.data.licensee_email,
        role: prev?.role || "", 
      }));
      setValidationState('valid');
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      const message = error.response?.data?.message || 'Ocorreu um erro ao validar seu convite.';
      setErrorMessage(message);
      setValidationState('invalid');
    }
  }

  async function resendCode() {
    try {
      setLoading(true);

      const body = {
        email: authFormData?.email,
      };

      await api.post("/users/resend-code", body);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }


  async function handleSubmitCode(code: string) {
    /*
    try {
      setLoading(true);

      if (!authFormData?.email || !code || !authFormData?.password) {
        showToast({
          type: "error",
          message: "Missing data",
        });
        return;
      }

      const body = {
        email: authFormData.email,
        code,
        password: authFormData.password,
      };

      const response = await validateCode(body);
      setUserId(response.id);
      nextStep();
    } catch (error) {
      showToast({
        type: "error",
        message: i18n.t("userRegistration.validCode"),
      });
    } finally {
      setLoading(false);
    }
    */
  }

  async function handleCreateUserAndSendValidationMail() {
    try {
      setLoading(true);

      if (!authFormData?.email || !authFormData?.password) {
        showToast({
          type: "error",
          message: "Missing data",
        });
        return;
      }

      const body = {
        email: authFormData.email,
        password: authFormData.password,
        registration_token: token ?? "",
        baseApiUrl: process.env.REACT_APP_BASE_URL || 'http://localhost:8080'
      };

      const response = await validateCode(body)

      setUserId(response.id);
      nextStep();
    } catch (error) {
      showToast({
        type: "error",
        message: i18n.t("userRegistration.emailInUse"),
      });
    } finally {
      setLoading(false);
    }
  }


  async function handleDeleteCode() {
    try {
      setLoading(false);

      const body = {
        email: authFormData?.email,
      };

      await api.post("/users/delete-temporary-user", body);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  async function handleCreatePerson(continueAsNatural: boolean) {
    setLoading(true);
    try {
      const body = {
        user: {
          id: userId,
        },
        name: personFormData?.name ?? "",
        cpf: personFormData?.cpf ?? "",
        country: personFormData?.country.id ?? "",
        cellphone: personFormData?.cellphone ?? "",
        state: personFormData?.state ?? "",
        city: personFormData?.city ?? "",
        expertise: personFormData?.expertise ?? "",
        professional_number: personFormData?.professional_number ?? "",
      };

      await api.post("/person", body).then((response) => {
        handleCreateLicensee(continueAsNatural, response.data.id);
      });
    } catch (error) {
      showToast({
        type: "error",
        message: i18n.t("userRegistration.errorCreatePerson"),
      });
      return;
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateLicensee(
    continueAsNatural: boolean,
    person_id: string
  ) {
    setLoading(true);

    if (addressFormData) {
      addresses[0] = {
        ...addressFormData,
        billing_address: true,
        deliver_address: true,
        company_address: !continueAsNatural
      }
    }

    if (companyAddressFormData) {
      addresses[1] = {
        ...companyAddressFormData,
        billing_address: false,
        deliver_address: false,
        company_address: true
      }
    }

    try {
      const body = {
        person: {
          id: person_id,
        },
        printers: [],
        instagram: "",
        email: continueAsNatural
          ? authFormData?.email
          : licenseeFormData?.email,
        country: continueAsNatural
          ? personFormData?.country?.id
          : licenseeFormData?.country?.id,
        phone: continueAsNatural
          ? personFormData?.cellphone
          : licenseeFormData?.phone ?? "",
        document_number: continueAsNatural
          ? personFormData?.cpf
          : licenseeFormData?.cnpj,
        situation: customerSituations.onboarding,
        city: continueAsNatural ? personFormData?.city : licenseeFormData?.city,
        state: continueAsNatural
          ? personFormData?.state
          : licenseeFormData?.state,
        name: continueAsNatural
          ? personFormData?.name
          : licenseeFormData?.fantasyName,
        manager_name: personFormData?.name,
        companyName: continueAsNatural
          ? personFormData?.name
          : licenseeFormData?.companyName,
        contract: "monthly",
        person_type: continueAsNatural
          ? personTypes.FISICA
          : personTypes.JURIDICA,
        is_public: true,
        termsAccepted: licenseeFormData?.termsAccepted,
      };

      await api.post("/licensees", body).then(async (response) => {
        addresses.forEach((data: IAddressFormData) => handleCreateAddress(response.data.id, data))
        setSelectedPrice(response.data.country);

        await api
          .get(`/user_terms/${response.data.contract}/active`)
          .then((response) => {
            api
              .post("/users/verify-registration", {
                user_term_id: response.data.user_term.data.id,
              })
              .catch((err) => console.error("err", err));
          });
      });
    } catch (error) {
      console.error(error)
    } finally {
      signOut()
      setLoading(false);
    }
  }

  async function handleCreateAddress(licensee_id: string, data: IAddressFormData) {
    setLoading(true);
    try {
      const body = {
        licensee_id,
        type: addressTypes.other,
        address: data?.address,
        number: data?.number,
        cep: data?.cep,
        complement: data?.complement ?? "",
        district: data?.district,
        city: data?.city,
        state: data?.state,
        country: data?.country.id,
        billing_address: data.billing_address,
        deliver_address: data.deliver_address,
        company_address: data.company_address
      };

      await api.post(`/address`, body);
    } catch (err: any) {
      showToast({
        type: "error",
        message:
          "Não foi possível cadastrar o endereço. Entre em contato com o SAF.",
      });
    } finally {
      setLoading(false);
    }
  }

  function nextStep() {
    if (activeStep === 3) return;
    setActiveStep((prevstate) => prevstate + 1);
  }

  function prevStep() {
    if (activeStep === 2) return;
    setActiveStep((prevstate) => prevstate - 1);
  }


  function handlePriceChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setSelectedPrice(e.target.value);
  }

  function verifyUserState() {
    var tmpActiveStep = 1;

    if (user?.role) {
      setUserId(user?.id);
      setAuthFormData({
        email: user.email,
        role: user.role,
      });
      tmpActiveStep = 2;
    }

    if (user?.role && user?.person) {

      const tmpCountry = countries.find((c) => c.code === user.person.country);
      setPersonFormData({
        ...user.person,
        country: {
          id: tmpCountry?.code || "BRA",
          label: tmpCountry?.label || "Brazil",
        },
      });
    }
    setActiveStep(tmpActiveStep);
  }

  useEffect(() => {
    if (token) {
      validateInvitationToken();
    } else {
      verifyUserState();
      setValidationState('valid');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, licensee]);

  return (
    <Container>
      <CustomToast />
      <LogoContainer>
        <img src={LogoImg} alt="Logo Fixit" />
      </LogoContainer>

      <FormContainer>
        {validationState === 'validating' && (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <IconWrapper color="#6c757d"><FiClock size={40} /></IconWrapper>
            <Title>Validando convite...</Title>
          </div>
        )}

        {validationState === 'invalid' && (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <IconWrapper color="#e53e3e"><FiXCircle size={40} /></IconWrapper>
            <Title>Erro no Convite</Title>
            <Message>{errorMessage}</Message>
            <Actions>
              <SolidButton text="Voltar para a Home" onClick={() => navigate('/')} />
            </Actions>
          </div>
        )}

        {validationState === 'valid' && (
          <StepperContainer>
            <Stepper activeStep={activeStep} steps={steps} />
          </StepperContainer>
        )}
      </FormContainer>

      <ChangeLanguageContainer>
        <LanguageSelector position="top" variant="transparent" />
      </ChangeLanguageContainer>

      <ChangePriceContainer>
        <PriceSelect
          onChange={(e) => {
            handlePriceChange(e);
          }}
          defaultValue={selectedPrice}
          value={selectedPrice}
        >
          <option value="BRA">💰 BRL</option>
          <option value="USD">💰 US</option>
        </PriceSelect>
      </ChangePriceContainer>
    </Container>
  );
}
